import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  LinearProgress,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";

import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import PhoneIcon from "@mui/icons-material/Phone";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import SpeedIcon from "@mui/icons-material/Speed";
import SatelliteAltIcon from "@mui/icons-material/SatelliteAlt";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PowerIcon from "@mui/icons-material/Power";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import InventoryServices from "../services/InventoryServices";
import { decipherEncryption } from "../helper";



// ============================================================
// HELPERS
// ============================================================

const formatDateTime = (timestamp) => {
  if (!timestamp) return "N/A";

  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) {
    return "N/A";
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};


const formatAgo = (seconds) => {
  if (seconds === null || seconds === undefined) {
    return "N/A";
  }

  const sec = Number(seconds);

  if (Number.isNaN(sec)) {
    return "N/A";
  }

  if (sec < 60) {
    return `${Math.max(0, Math.floor(sec))}s ago`;
  }

  const minutes = Math.floor(sec / 60);

  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  const hours = Math.floor(minutes / 60);

  if (hours < 24) {
    return `${hours}h ago`;
  }

  const days = Math.floor(hours / 24);

  return `${days}d ago`;
};


const getSignalColor = (tier) => {
  switch (tier) {
    case "good":
      return "#0aa31a";

    case "fair":
      return "#e09b00";

    case "weak":
      return "#e53935";

    default:
      return "#777";
  }
};


const getBatteryPercentage = (voltage, type) => {
  const value = Number(voltage);

  if (!Number.isFinite(value)) {
    return 0;
  }

  if (type === "internal") {
    // Approximate visual percentage for display only.
    const min = 2.8;
    const max = 4.2;

    return Math.max(
      0,
      Math.min(100, ((value - min) / (max - min)) * 100)
    );
  }

  // External battery is variable in the API,
  // so use a visual scale rather than treating it
  // as an exact battery percentage.
  const min = 0;
  const max = 14;

  return Math.max(
    0,
    Math.min(100, (value / max) * 100)
  );
};


// ============================================================
// DEVICE CARD
// ============================================================

const DeviceCard = ({ device,onReboot,
    rebooting, userRole, currentTime, }) => {
  const telemetry = device.telemetry || {};
  const location =
    device.last_location_valid ||
    device.last_location_any ||
    {};
const lastAlert = device.last_alert || {};
    // ==========================================================
// LIVE GPS TIME
// ==========================================================

const getLiveSecondsAgo = (timestamp, fallbackSeconds) => {
    if (!timestamp) {
        return fallbackSeconds;
    }

    const timestampMs = new Date(timestamp).getTime();

    if (Number.isNaN(timestampMs)) {
        return fallbackSeconds;
    }

    return Math.max(
        0,
        Math.floor((currentTime - timestampMs) / 1000)
    );
};

const liveGpsSecondsAgo = getLiveSecondsAgo(
    location.timestamp,
    location.seconds_ago
);

// ==========================================================
// LIVE ALERT TIME
// ==========================================================

const liveAlertSecondsAgo = getLiveSecondsAgo(
    lastAlert.timestamp,
    lastAlert.seconds_ago
);

  // const lastAlert = device.last_alert || {};

  // const isStale =
  //   Number(location.seconds_ago) > 24 * 60 * 60;

  const isStale =
    Number(liveGpsSecondsAgo) > 24 * 60 * 60;

  const internalBatteryPercentage = getBatteryPercentage(
    telemetry.internal_battery_voltage,
    "internal"
  );

  const externalBatteryPercentage = getBatteryPercentage(
    telemetry.external_battery_voltage,
    "external"
  );

  const internalBatteryLow =
    telemetry.internal_battery_tier === "critical" ||
    Number(telemetry.internal_battery_voltage) < 3.5;

  const externalBatteryLow =
    telemetry.external_battery_tier === "critical" ||
    Number(telemetry.external_battery_voltage) < 11.5;

  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: "16px",
        p: 2.2,
        backgroundColor: "#fff",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
      }}
    >
      {/* =====================================================
          HEADER
      ====================================================== */}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 1,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: 700,
              color: "#111",
              lineHeight: 1.2,
            }}
          >
            {device.vehicle_reg_no || "N/A"}
          </Typography>

          <Typography
            sx={{
              fontSize: "14px",
              color: "#555",
              mt: 0.6,
            }}
          >
            {device.dealer_name || "N/A"}
          </Typography>

          <Typography
            sx={{
              fontSize: "13px",
              color: "#777",
              mt: 0.4,
              fontFamily: "monospace",
            }}
          >
            {device.imei || "N/A"}
          </Typography>

          <Typography
            sx={{
              fontSize: "13px",
              color: "#777",
              mt: 0.4,
              fontFamily: "monospace",
            }}
          >
            {device.msisdn1 || "N/A"} / {device.msisdn1 || "N/A"}
          </Typography>

         
        </Box>

        <Chip
          label={device.stock_status || "Unknown"}
          size="small"
          sx={{
            backgroundColor: "#eef8ff",
            color: "#0086b3",
            fontWeight: 600,
            borderRadius: "14px",
          }}
        />
      </Box>


      {/* =====================================================
          OWNER MOBILE
      ====================================================== */}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mt: 1.5,
        }}
      >
        <PhoneIcon
          sx={{
            fontSize: 18,
            color: "#555",
          }}
        />

        <Typography
          sx={{
            color: "#008b95",
            fontWeight: 700,
            fontSize: "14px",
          }}
        >
          {device.owner_mobile_no || "N/A"}
        </Typography>
      </Box>


      {/* =====================================================
          STATUS CHIPS
      ====================================================== */}

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.8,
          mt: 1.5,
        }}
      >
        <Chip
          label={
            telemetry.ignition_on
              ? "Ignition ON"
              : "Ignition OFF"
          }
          size="small"
          sx={{
            fontWeight: 600,
            backgroundColor: telemetry.ignition_on
              ? "#e5f8e6"
              : "#f1f1f1",
            color: telemetry.ignition_on
              ? "#009b08"
              : "#555",
          }}
        />

        <Chip
          label={
            telemetry.main_power_on
              ? "Main Power ON"
              : "Main Power OFF"
          }
          size="small"
          sx={{
            fontWeight: 600,
            backgroundColor: telemetry.main_power_on
              ? "#e5f8e6"
              : "#fde7e7",
            color: telemetry.main_power_on
              ? "#009b08"
              : "#d32f2f",
          }}
        />

        <Chip
          label={
            location.is_valid
              ? "GPS Fix OK"
              : "GPS Loss"
          }
          size="small"
          sx={{
            fontWeight: 600,
            backgroundColor: location.is_valid
              ? "#e5f8e6"
              : "#fde7e7",
            color: location.is_valid
              ? "#009b08"
              : "#d32f2f",
          }}
        />

        {telemetry.box_tamper_open && (
          <Chip
            label="Tamper OPEN"
            size="small"
            sx={{
              fontWeight: 700,
              backgroundColor: "#fde7e7",
              color: "#d32f2f",
            }}
          />
        )}

        {telemetry.sos_active && (
          <Chip
            label="SOS ACTIVE"
            size="small"
            sx={{
              fontWeight: 700,
              backgroundColor: "#fde7e7",
              color: "#d32f2f",
            }}
          />
        )}
      </Box>


      {/* =====================================================
          SPEED / SATELLITES / ALTITUDE
      ====================================================== */}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 1,
          mt: 1.5,
        }}
      >
        <InfoBox
          value={Number(telemetry.speed || 0).toFixed(0)}
          label="KM/H"
        />

        <InfoBox
          value={telemetry.satellites ?? 0}
          label="SATELLITES"
        />

        <InfoBox
          value={telemetry.altitude ?? 0}
          label="ALTITUDE M"
        />
      </Box>


      {/* =====================================================
          GSM / NETWORK
      ====================================================== */}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mt: 1.5,
        }}
      >
        <SignalCellularAltIcon
          sx={{
            color: getSignalColor(
              telemetry.gsm_signal_tier
            ),
          }}
        />

        <Typography
          sx={{
            fontSize: "14px",
            color: "#555",
          }}
        >
          <Box
            component="span"
            sx={{
              color: getSignalColor(
                telemetry.gsm_signal_tier
              ),
              fontWeight: 700,
            }}
          >
            {telemetry.network_operator || "NO NETWORK"}
          </Box>

          {" · "}

          {telemetry.gsm_signal_raw ?? 0}
        </Typography>
      </Box>


      {/* =====================================================
          INTERNAL BATTERY
      ====================================================== */}

      <BatteryBar
        label="Internal battery"
        voltage={telemetry.internal_battery_voltage}
        percentage={internalBatteryPercentage}
        low={internalBatteryLow}
      />


      {/* =====================================================
          EXTERNAL BATTERY
      ====================================================== */}

      <BatteryBar
        label="External (main) power"
        voltage={telemetry.external_battery_voltage}
        percentage={externalBatteryPercentage}
        low={externalBatteryLow}
      />


      {/* =====================================================
          LAST ALERT
      ====================================================== */}

      <Box
        sx={{
          mt: 1.5,
          border: "1px solid #e0e0e0",
          borderRadius: "10px",
          px: 1.3,
          py: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "13px",
            color:
              lastAlert.status === "out"
                ? "#777"
                : "#555",
          }}
        >
          {lastAlert.type
            ? `${lastAlert.type} ${
                lastAlert.status === "out"
                  ? "(cleared)"
                  : ""
              }`
            : "No alert"}
        </Typography>

        <Typography
          sx={{
            fontSize: "12px",
            color: isStale ? "#d32f2f" : "#888",
            fontWeight: isStale ? 700 : 400,
            whiteSpace: "nowrap",
          }}
        >
          {formatAgo(liveAlertSecondsAgo)}
        </Typography>
      </Box>


      {/* =====================================================
          GPS LOCATION
      ====================================================== */}

      <Box
        sx={{
          mt: 1.5,
          pb: 1.2,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Typography
          sx={{
            fontSize: "13px",
            fontFamily: "monospace",
            color: "#333",
          }}
        >
          {location.latitude ?? "N/A"},{" "}
          {location.longitude ?? "N/A"}
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            mt: 0.3,
          }}
        >
          <LocationOnIcon
            sx={{
              fontSize: 15,
              color: "#777",
            }}
          />

          <Typography
            sx={{
              fontSize: "12px",
              color: "#555",
            }}
          >
            {formatDateTime(location.timestamp)}
          </Typography>

          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 700,
              color: isStale
                ? "#d32f2f"
                : "#333",
              ml: 0.5,
            }}
          >
            · {formatAgo(liveGpsSecondsAgo)}
          </Typography>
        </Box>
      </Box>


      {/* =====================================================
          REBOOT
      ====================================================== */}


<Box sx={{ mt: 1.4 }}>
    <Button
        variant="contained"
        size="small"
        startIcon={
            rebooting ? (
                <CircularProgress
                    size={16}
                    sx={{ color: "#e53935" }}
                />
            ) : (
                <PowerIcon />
            )
        }
        onClick={() => onReboot(device)}
        disabled={
    userRole !== "superadmin" || rebooting
}
        sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "9px",
            backgroundColor: "#fde1e1",
            color: "#e53935",
            boxShadow: "none",

            "&:hover": {
                backgroundColor: "#fbd2d2",
                boxShadow: "none",
            },

            "&.Mui-disabled": {
                backgroundColor: "#fde1e1",
                color: "#e53935",
            },
        }}
    >
        {rebooting ? "Rebooting..." : "Reboot device"}
    </Button>
</Box>
    </Paper>
  );
};


// ============================================================
// INFO BOX
// ============================================================

const InfoBox = ({ value, label }) => {
  return (
    <Box
      sx={{
        border: "1px solid #dedede",
        borderRadius: "12px",
        minHeight: "56px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Typography
        sx={{
          fontSize: "18px",
          fontWeight: 700,
          color: "#111",
          lineHeight: 1.2,
        }}
      >
        {value}
      </Typography>

      <Typography
        sx={{
          fontSize: "10px",
          color: "#777",
          mt: 0.3,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};


// ============================================================
// BATTERY BAR
// ============================================================

const BatteryBar = ({
  label,
  voltage,
  percentage,
  low,
}) => {
  return (
    <Box sx={{ mt: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 0.5,
        }}
      >
        <Typography
          sx={{
            fontSize: "13px",
            color: "#555",
          }}
        >
          {label}
        </Typography>

        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 700,
            color: low ? "#d32f2f" : "#111",
          }}
        >
          {voltage ?? 0} V
        </Typography>
      </Box>

      <LinearProgress
        variant="determinate"
        value={percentage}
        sx={{
          height: 7,
          borderRadius: 10,
          backgroundColor: "#dededb",
          "& .MuiLinearProgress-bar": {
            borderRadius: 10,
            backgroundColor: low
              ? "#d32f2f"
              : "#08a817",
          },
        }}
      />
    </Box>
  );
};


// ============================================================
// MAIN COMPONENT
// ============================================================

const DeviceHealth = () => {
  const [devices, setDevices] = useState([]);
  const getUserRole = () => {
    try {
        const myDecipher = decipherEncryption("skytrack");

        const userData =
            sessionStorage.getItem("cookiesData") ||
            localStorage.getItem("cookiesData");

        if (!userData) {
            return "";
        }

        const data = userData
            .split("-")
            .map((item) => myDecipher(item));

        return String(data?.[1] || "")
            .toLowerCase()
            .trim();

    } catch (error) {
        console.error("Unable to read user role:", error);
        return "";
    }
};

const userRole = getUserRole();

  const [manufacturer, setManufacturer] = useState("");

  const [loading, setLoading] = useState(false);
  const [rebootingImei, setRebootingImei] = useState(null);

  const [search, setSearch] = useState("");

  const [activeFilter, setActiveFilter] =
    useState("");

  const [serverTime, setServerTime] = useState(null);
const [currentTime, setCurrentTime] = useState(Date.now());
  const [error, setError] = useState("");

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
  });

  // ==========================================================
// LIVE TIME COUNTER
// ==========================================================

useEffect(() => {
    const timer = setInterval(() => {
        setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(timer);
}, []);

  // ==========================================================
  // FETCH DEVICES
  // ==========================================================

 const fetchDevices = useCallback(async () => {
    try {
        setLoading(true);
        setError("");

        const response =
            await InventoryServices.deviceHealth();

        const data = response?.data;

        setDevices(
            Array.isArray(data?.devices)
                ? data.devices
                : []
        );

        setManufacturer(
            data?.manufacturer || ""
        );

        setServerTime(
            data?.server_time || null
        );

        setSnackbar({
            open: true,
            message: `Device data refreshed successfully. ${
                data?.count || 0
            } devices found.`,
        });

    } catch (err) {
        console.error(
            "Device Health API Error:",
            err
        );

        setError(
            err?.response?.data?.message ||
            err?.message ||
            "Unable to load device data."
        );

        setSnackbar({
            open: true,
            message:
                "Unable to load device health data.",
        });

    } finally {
        setLoading(false);
    }
}, []);

const handleReboot = useCallback(async (device) => {
    const imei = device?.imei;

    if (!imei) {
        setSnackbar({
            open: true,
            message: "IMEI is not available for this device.",
        });

        return;
    }

    try {
        setRebootingImei(imei);

        const response = await InventoryServices.rebootDevice({
            imei: imei,
            payload: "@REBOOT*",
        });

        const data = response?.data;

        if (
            data?.status === "success" &&
            data?.result === "delivered"
        ) {
            setSnackbar({
                open: true,
                message:
                    data?.detail ||
                    "Reboot command sent successfully.",
            });
        } else {
            setSnackbar({
                open: true,
                message:
                    data?.detail ||
                    "Reboot command could not be delivered.",
            });
        }

    } catch (err) {
        console.error(
            "Reboot Device API Error:",
            err
        );

        setSnackbar({
            open: true,
            message:
                err?.response?.data?.detail ||
                err?.response?.data?.message ||
                err?.message ||
                "Unable to send reboot command.",
        });

    } finally {
        setRebootingImei(null);
    }
}, []);

  // ==========================================================
  // INITIAL API CALL
  // ==========================================================

 // INITIAL API CALL ONLY
useEffect(() => {
    fetchDevices();
}, [fetchDevices]);


  // ==========================================================
  // SUMMARY
  // ==========================================================

  const summary = useMemo(() => {
    const total = devices.length;

    const online = devices.filter((device) => {
      const seconds =
        device?.last_location_valid?.seconds_ago ??
        device?.last_location_any?.seconds_ago;

      return (
        Number.isFinite(Number(seconds)) &&
        Number(seconds) <= 15 * 60
      );
    }).length;

    const ignitionOn = devices.filter(
      (device) =>
        device?.telemetry?.ignition_on === true
    ).length;

    const sosActive = devices.filter(
      (device) =>
        device?.telemetry?.sos_active === true
    ).length;

    const stale = devices.filter((device) => {
      const seconds =
        device?.last_location_valid?.seconds_ago ??
        device?.last_location_any?.seconds_ago;

      return Number(seconds) > 24 * 60 * 60;
    }).length;

    return {
      total,
      online,
      ignitionOn,
      sosActive,
      stale,
    };
  }, [devices]);


  // ==========================================================
  // FILTER
  // ==========================================================

  const filteredDevices = useMemo(() => {
    let result = [...devices];

    // Search
    const searchValue =
      search.trim().toLowerCase();

    if (searchValue) {
      result = result.filter((device) => {
        return (
          String(
            device.vehicle_reg_no || ""
          )
            .toLowerCase()
            .includes(searchValue) ||
          String(
            device.dealer_name || ""
          )
            .toLowerCase()
            .includes(searchValue) ||
          String(device.imei || "")
            .toLowerCase()
            .includes(searchValue)
        );
      });
    }


    // Button filters
    switch (activeFilter) {
      case "network-loss":
        result = result.filter(
          (device) =>
            device?.last_alert?.type ===
              "NetworkLoss" &&
            device?.last_alert?.status ===
              "out"
        );
        break;

      case "gps-loss":
        result = result.filter((device) => {
          const location =
            device?.last_location_valid ||
            device?.last_location_any;

          return (
            !location ||
            location.is_valid !== true
          );
        });
        break;

      case "tamper":
        result = result.filter(
          (device) =>
            device?.telemetry
              ?.box_tamper_open === true
        );
        break;

      case "low-internal":
        result = result.filter((device) => {
          const tier =
            device?.telemetry
              ?.internal_battery_tier;

          const voltage = Number(
            device?.telemetry
              ?.internal_battery_voltage
          );

          return (
            tier === "critical" ||
            voltage < 3.5
          );
        });
        break;

      case "low-external":
        result = result.filter((device) => {
          const tier =
            device?.telemetry
              ?.external_battery_tier;

          const voltage = Number(
            device?.telemetry
              ?.external_battery_voltage
          );

          return (
            tier === "critical" ||
            voltage < 11.5
          );
        });
        break;

      case "not-live":
        result = result.filter((device) => {
          const location =
            device?.last_location_valid ||
            device?.last_location_any;

          return (
            Number(location?.seconds_ago) >
            5 * 60
          );
        });
        break;

      case "low-network":
        result = result.filter((device) => {
          const signal = Number(
            device?.telemetry
              ?.gsm_signal_raw
          );

          return signal < 22;
        });
        break;

      default:
        break;
    }

    return result;
  }, [
    devices,
    search,
    activeFilter,
  ]);


  // ==========================================================
  // FILTER BUTTON
  // ==========================================================

  const handleFilter = (filter) => {
    setActiveFilter((current) =>
      current === filter ? "" : filter
    );
  };


  // ==========================================================
  // CLEAR FILTER
  // ==========================================================

  const clearFilters = () => {
    setSearch("");
    setActiveFilter("");
  };


  // ==========================================================
  // UPDATED TIME
  // ==========================================================

  const updatedText = serverTime
    ? new Date(serverTime).toLocaleTimeString(
        "en-IN",
        {
          hour: "numeric",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        }
      )
    : "—";


  // ==========================================================
  // UI
  // ==========================================================

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#fafafa",
        px: {
          xs: 1.5,
          sm: 2.5,
          md: 3,
        },
        py: 3,
        boxSizing: "border-box",
      }}
    >
      {/* ====================================================
          PAGE HEADER
      ===================================================== */}

      <Typography
        sx={{
          fontSize: {
            xs: "25px",
            md: "28px",
          },
          fontWeight: 700,
          color: "#111",
        }}
      >
        Device Health
      </Typography>

      <Typography
        sx={{
          mt: 0.5,
          color: "#555",
          fontSize: "15px",
          maxWidth: "1100px",
          lineHeight: 1.5,
        }}
      >
        Live status for every tagged device under the{" "}
        {manufacturer || "-"}
        {" "}manufacturer (tag status
        Owner_Final_OTP_Verified): owner contact,
        speed, GSM signal & network, GPS satellites,
        ignition, SOS, internal/external battery,
        tamper, last alert and GPS fix validity.
        No login required.
      </Typography>


      {/* ====================================================
          ACTION BAR
      ===================================================== */}

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1.2,
          mt: 2,
        }}
      >
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={fetchDevices}
          disabled={loading}
          sx={{
            height: 50,
            px: 2.5,
            textTransform: "none",
            fontSize: "16px",
            borderRadius: "10px",
            backgroundColor: "#08a6a6",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#078f8f",
              boxShadow: "none",
            },
          }}
        >
          {loading ? "Refreshing..." : "Refresh"}
        </Button>


        <TextField
          value={search}
          onChange={(event) =>
            setSearch(event.target.value)
          }
          placeholder="Filter by reg no, dealer or IMEI"
          size="small"
          sx={{
            width: {
              xs: "100%",
              sm: 300,
              md: 350,
            },
            "& .MuiOutlinedInput-root": {
              height: 50,
              borderRadius: "10px",
              backgroundColor: "#fff",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  sx={{ color: "#777" }}
                />
              </InputAdornment>
            ),
          }}
        />


        <Typography
          sx={{
            color: "#555",
            fontSize: "14px",
          }}
        >
          {manufacturer || "-"}
          {" "} - {devices.length} device(s)
          {" "} - updated {updatedText}
        </Typography>
      </Box>


      {/* ====================================================
          FILTERS
      ===================================================== */}

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          mt: 2,
        }}
      >
        <FilterButton
          label="Network Loss"
          active={
            activeFilter === "network-loss"
          }
          onClick={() =>
            handleFilter("network-loss")
          }
        />

        <FilterButton
          label="GPS Loss"
          active={
            activeFilter === "gps-loss"
          }
          onClick={() =>
            handleFilter("gps-loss")
          }
        />

        <FilterButton
          label="Tamper Open"
          active={
            activeFilter === "tamper"
          }
          onClick={() =>
            handleFilter("tamper")
          }
        />

        <FilterButton
          label="Low Internal Battery"
          active={
            activeFilter === "low-internal"
          }
          onClick={() =>
            handleFilter("low-internal")
          }
        />

        <FilterButton
          label="Low External Battery"
          active={
            activeFilter === "low-external"
          }
          onClick={() =>
            handleFilter("low-external")
          }
        />

        <FilterButton
          label="Not Live (>5m)"
          active={
            activeFilter === "not-live"
          }
          onClick={() =>
            handleFilter("not-live")
          }
        />

        <FilterButton
          label="Low Network (<22)"
          active={
            activeFilter === "low-network"
          }
          onClick={() =>
            handleFilter("low-network")
          }
        />

        <Button
          variant="outlined"
          onClick={clearFilters}
          sx={{
            borderColor: "#ddd",
            color: "#555",
            textTransform: "none",
            borderRadius: "20px",
            px: 2,
            backgroundColor: "#fff",
            "&:hover": {
              borderColor: "#bbb",
              backgroundColor: "#f8f8f8",
            },
          }}
        >
          Clear filters
        </Button>
      </Box>


      {/* ====================================================
          SUMMARY CARDS
      ===================================================== */}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(5, 1fr)",
          },
          gap: 1.5,
          mt: 2.5,
        }}
      >
        <SummaryCard
          value={summary.total}
          label="TOTAL DEVICES"
          valueColor="#111"
        />

        <SummaryCard
          value={summary.online}
          label="ONLINE (≤15M)"
          valueColor="#0a9f17"
        />

        <SummaryCard
          value={summary.ignitionOn}
          label="IGNITION ON"
          valueColor="#008d8d"
        />

        <SummaryCard
          value={summary.sosActive}
          label="SOS ACTIVE"
          valueColor="#d73737"
        />

        <SummaryCard
          value={summary.stale}
          label="STALE (>24H)"
          valueColor="#d73737"
        />
      </Box>


      {/* ====================================================
          ERROR
      ===================================================== */}

      {error && (
        <Alert
          severity="error"
          sx={{
            mt: 2,
            borderRadius: "10px",
          }}
        >
          {error}
        </Alert>
      )}


      {/* ====================================================
          LOADING
      ===================================================== */}

      {loading && devices.length === 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 8,
          }}
        >
          <CircularProgress />
        </Box>
      )}


      {/* ====================================================
          DEVICE GRID
      ===================================================== */}

      {!loading &&
        filteredDevices.length === 0 && (
          <Paper
            elevation={0}
            sx={{
              mt: 3,
              p: 5,
              textAlign: "center",
              border: "1px solid #e0e0e0",
              borderRadius: "15px",
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                color: "#555",
              }}
            >
              No devices found
            </Typography>

            <Typography
              sx={{
                fontSize: "14px",
                color: "#888",
                mt: 0.5,
              }}
            >
              Try changing your search or filters.
            </Typography>
          </Paper>
        )}


      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            lg: "repeat(3, 1fr)",
          },
          gap: 1.8,
          mt: 2.5,
          alignItems: "stretch",
        }}
      >
        {filteredDevices.map((device) => (
          <DeviceCard
            key={device.imei}
            device={device}
             onReboot={handleReboot}
        rebooting={rebootingImei === device.imei}
         userRole={userRole}
         currentTime={currentTime}

          />
        ))}
      </Box>


      {/* ====================================================
          SNACKBAR
      ===================================================== */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() =>
          setSnackbar({
            open: false,
            message: "",
          })
        }
        anchorOrigin={{
        vertical: "top",
        horizontal: "right",
    }}
        message={snackbar.message}
      />
    </Box>
  );
};


// ============================================================
// FILTER BUTTON
// ============================================================

const FilterButton = ({
  label,
  active,
  onClick,
}) => {
  return (
    <Button
      variant="outlined"
      onClick={onClick}
      sx={{
        textTransform: "none",
        borderRadius: "20px",
        px: 2,
        minHeight: 40,
        borderColor: active
          ? "#008f8f"
          : "#dedede",
        backgroundColor: active
          ? "#e7f8f8"
          : "#fff",
        color: active
          ? "#007d7d"
          : "#555",
        fontWeight: active ? 700 : 500,
        "&:hover": {
          borderColor: "#008f8f",
          backgroundColor: "#eefafa",
        },
      }}
    >
      {label}
    </Button>
  );
};


// ============================================================
// SUMMARY CARD
// ============================================================

const SummaryCard = ({
  value,
  label,
  valueColor,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: "15px",
        p: 2,
        minHeight: 88,
        boxSizing: "border-box",
      }}
    >
      <Typography
        sx={{
          fontSize: "34px",
          fontWeight: 700,
          lineHeight: 1,
          color: valueColor,
        }}
      >
        {value}
      </Typography>

      <Typography
        sx={{
          mt: 0.8,
          fontSize: "13px",
          color: "#555",
        }}
      >
        {label}
      </Typography>
    </Paper>
  );
};


export default DeviceHealth;